// src/context/WebSocketContext.tsx - Solo agregando limpieza automática desde machineStateUpdate

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import io, { Socket } from "socket.io-client";
import { toast } from "react-toastify";
import {
  UserData,
  ClientStateData,
  CommandData,
  CommandResponse,
} from "../types/websocket";

// Interfaz mantenida EXACTAMENTE igual para compatibilidad
interface Machine {
  uuid: string;
  nro_maquina?: string;
  id_machine?: string;
  online?: boolean;
  statusMachine?: 0 | 1 | "unknown";
}

// NUEVA: Interfaz para callbacks de limpieza
interface MachineCleanupCallbacks {
  onMachineDisabled?: (uuid: string) => void;
}

// Interfaz del contexto actualizada
interface WebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  isMachineSessionStarted: boolean;
  isUserSessionStarted: boolean;
  machines: Machine[];
  onlineMachines: string[];
  connect: () => void;
  disconnect: () => void;
  disconnectMachine: () => void;
  emit: (eventName: string, data: any) => void;
  sendCommand: (
    commandData: CommandData,
    onResponse?: (response: CommandResponse) => void,
    timeout?: number
  ) => void;
  getMachinesFromSocket: () => void;
  subscribeMachineUpdates?: (uuid: string) => void;
  unsubscribeMachineUpdates?: (uuid: string) => void;
  // NUEVO: Registrar callbacks de limpieza
  registerCleanupCallbacks?: (callbacks: MachineCleanupCallbacks) => void;
}

interface WebSocketProviderProps {
  children: React.ReactNode;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined
);

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
}) => {
  // Estados originales mantenidos EXACTAMENTE igual
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isMachineSessionStarted, setIsMachineSessionStarted] = useState(false);
  const [isUserSessionStarted, setIsUserSessionStarted] = useState(false);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [onlineMachines, setOnlineMachines] = useState<string[]>([]);

  // Referencia para comandos pendientes
  const pendingCommandsRef = useRef(
    new Map<
      string,
      {
        onResponse: (response: CommandResponse) => void;
        timeoutId: ReturnType<typeof setTimeout>;
      }
    >()
  );

  // NUEVO: Referencia para callbacks de limpieza
  const cleanupCallbacksRef = useRef<MachineCleanupCallbacks>({});

  // NUEVA función para registrar callbacks
  const registerCleanupCallbacks = useCallback(
    (callbacks: MachineCleanupCallbacks) => {
      cleanupCallbacksRef.current = {
        ...cleanupCallbacksRef.current,
        ...callbacks,
      };
    },
    []
  );

  // Función original mantenida
  const getUserAndClientData = () => {
    const storedUser = localStorage.getItem("user");
    const storedClient = localStorage.getItem("currentClient");
    const user: UserData = storedUser ? JSON.parse(storedUser) : null;
    const client: ClientStateData = storedClient
      ? JSON.parse(storedClient)
      : null;
    return { user, client };
  };

  // Función original mantenida + optimizaciones internas
  const getMachinesFromSocket = useCallback(() => {
    if (socket && socket.connected) {
      console.log("Solicitando lista de máquinas al servidor...");
      socket.emit("getMachines", "");
    } else {
      console.warn("No se puede solicitar listado: Socket no conectado");
    }
  }, [socket]);

  // Función de optimización (opcional, no afecta compatibilidad)
  const updateMachineStatus = useCallback(
    (uuid: string, statusUpdate: Partial<Machine>) => {
      if (!uuid) return;

      setMachines((prev) => {
        const index = prev.findIndex((m) => m.uuid === uuid);

        if (index >= 0) {
          // Verificar si realmente hay cambios antes de actualizar
          const current = prev[index];
          const hasChanges = Object.keys(statusUpdate).some(
            (key) =>
              current[key as keyof Machine] !==
              statusUpdate[key as keyof Machine]
          );

          if (!hasChanges) return prev;

          const newMachines = [...prev];
          newMachines[index] = {
            ...newMachines[index],
            ...statusUpdate,
          };
          return newMachines;
        } else if (statusUpdate.nro_maquina || statusUpdate.id_machine) {
          return [...prev, { uuid, ...statusUpdate }];
        }

        return prev;
      });
    },
    []
  );

  // Funciones nuevas opcionales (no rompen compatibilidad)
  const subscribeMachineUpdates = useCallback(
    (uuid: string) => {
      if (socket && socket.connected) {
        socket.emit("subscribeMachineUpdates", uuid);
      }
    },
    [socket]
  );

  const unsubscribeMachineUpdates = useCallback(
    (uuid: string) => {
      if (socket && socket.connected) {
        socket.emit("unsubscribeMachineUpdates", uuid);
      }
    },
    [socket]
  );

  // Función connect original mantenida + optimizaciones
  const connect = () => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      console.warn("No hay usuario almacenado, no se puede conectar");
      return;
    }

    const userData = JSON.parse(storedUser);
    const wsUrl =
      userData.urlWebSocket || "https://kit-websocket.onrender.com/";

    try {
      console.log(`Conectando a WebSocket: ${wsUrl}`);
      const newSocket = io(wsUrl, {
        transports: ["websocket"],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      newSocket.on("connect", () => {
        console.log("Conectado exitosamente a WebSocket");
        setIsConnected(true);

        // Código original mantenido
        initializeMachineSession();

        // Identificarse como cliente para optimizaciones
        newSocket.emit("identifyClient", { type: "client" });

        // Solicitar lista original mantenida + timeout optimizado
        setTimeout(() => {
          getMachinesFromSocket();
        }, 500);
      });

      // Handler original mantenido EXACTAMENTE igual
      newSocket.on("disconnect", () => {
        console.log("Desconectado del WebSocket");
        setIsConnected(false);
        setIsMachineSessionStarted(false);
        setIsUserSessionStarted(false);

        setMachines((prev) =>
          prev.map((machine) => ({
            ...machine,
            online: false,
          }))
        );
        setOnlineMachines([]);
      });

      // Handlers originales mantenidos EXACTAMENTE igual
      newSocket.on("captureCommand", (data: string) => {
        console.log("Received command:", data);
      });

      newSocket.on("captureCommandMachine", (data: string) => {
        console.log("Received machine command:", data);
      });

      newSocket.on("loginMachine", (data: any) => {
        console.log("Machine login:", data);
        setIsMachineSessionStarted(true);
        getMachinesFromSocket();
      });

      newSocket.on("error", (error: any) => {
        console.error("WebSocket error:", error);
        toast.error("Error en la conexión con el servidor");
      });

      // Handlers nuevos que no rompen compatibilidad
      newSocket.on("allMachineStates", (states) => {
        console.log("Estados iniciales recibidos:", states);
        if (states && typeof states === "object") {
          const machineArray = Object.entries(states).map(
            ([uuid, state]: [string, any]) => {
              console.log(`Procesando máquina ${uuid}:`, state);
              return {
                uuid,
                nro_maquina: state.id_machine || state.nro_maquina || state.data?.id_machine,
                online: state.online || false,
                statusMachine: state.statusMachine !== undefined ? state.statusMachine : "unknown",
                ...state,
              };
            }
          );
          console.log("Array de máquinas procesado:", machineArray);
          setMachines(machineArray);

          const onlineUuids = machineArray
            .filter((machine) => machine.online)
            .map((machine) => machine.uuid);
          setOnlineMachines(onlineUuids);
        }
      });

      newSocket.on("machineStateUpdate", ({ uuid, state }) => {
        console.log(`Update de estado para máquina ${uuid}:`, state);

        // Asegurar que nro_maquina se incluya en el update
        const updateData = {
          ...state,
          nro_maquina: state.id_machine || state.nro_maquina || state.data?.id_machine,
        };

        updateMachineStatus(uuid, updateData);

        setOnlineMachines((prev) => {
          const filtered = prev.filter((id) => id !== uuid);
          return state.online ? [...filtered, uuid] : filtered;
        });

        // NUEVO: Si la máquina se deshabilitó (statusMachine cambió a 0), ejecutar limpieza
        if (
          state.statusMachine === 0 &&
          cleanupCallbacksRef.current.onMachineDisabled
        ) {
          try {
            cleanupCallbacksRef.current.onMachineDisabled(uuid);
            console.log(
              `Ejecutando limpieza automática para máquina ${uuid} - deshabilitada por machineStateUpdate`
            );
          } catch (error) {
            console.error("Error en callback de limpieza:", error);
          }
        }
      });

      setSocket(newSocket);
    } catch (error) {
      console.error("Error connecting to WebSocket:", error);
      toast.error("Error al conectar con el servidor");
    }
  };

  // Función original mantenida EXACTAMENTE igual
  const initializeMachineSession = () => {
    if (!socket || isMachineSessionStarted) return;

    const { user } = getUserAndClientData();
    if (!user) return;

    console.log("Inicializando sesión de máquina...");
  };

  // Funciones originales mantenidas EXACTAMENTE igual
  const disconnect = () => {
    if (socket) {
      const { user } = getUserAndClientData();
      if (user) {
        socket.emit("deleteUserMf", { id: user.id });
      }
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
    }
  };

  const disconnectMachine = () => {
    if (socket) {
      const { user } = getUserAndClientData();
      if (user) {
        socket.emit("deleteMachine", { id_machine: "1" });
      }
    }
  };

  const emit = (eventName: string, data: any) => {
    if (socket && isConnected) {
      socket.emit(eventName, data);
    } else {
      toast.error("No hay conexión con el servidor de máquinas");
    }
  };

  // FUNCIÓN ACTUALIZADA: handleCommandResponse con limpieza automática
  const handleCommandResponse = useCallback(
    (response: CommandResponse) => {
      const { uuid, maquina, command } = response;
      const commandKey = `${uuid}:${maquina}`;
      const pendingCommand = pendingCommandsRef.current.get(commandKey);

      // Lógica original mantenida + NUEVA limpieza automática
      if (uuid && response.response) {
        if (command === "enabledMachine") {
          updateMachineStatus(uuid, { statusMachine: 1 });
          console.log(`Máquina ${maquina} habilitada`);
        } else if (command === "disabledMachine") {
          updateMachineStatus(uuid, { statusMachine: 0 });
          console.log(`Máquina ${maquina} deshabilitada`);

          // NUEVO: Ejecutar callback de limpieza automática
          if (cleanupCallbacksRef.current.onMachineDisabled) {
            try {
              cleanupCallbacksRef.current.onMachineDisabled(uuid);
              console.log(
                `Ejecutando limpieza automática para máquina ${uuid} - respuesta de comando`
              );
            } catch (error) {
              console.error("Error en callback de limpieza:", error);
            }
          }
        }
      }

      if (pendingCommand) {
        clearTimeout(pendingCommand.timeoutId);
        pendingCommand.onResponse(response);
        pendingCommandsRef.current.delete(commandKey);
      }
    },
    [updateMachineStatus]
  );

  // useEffect original mantenido + nuevos handlers opcionales
  useEffect(() => {
    if (socket) {
      // Función original mantenida EXACTAMENTE igual
      const updateOnlineStatus = (onlineDevices: any[]) => {
        if (!Array.isArray(onlineDevices)) {
          console.warn(
            "listMachines: Datos recibidos no son un array",
            onlineDevices
          );
          return;
        }

        console.log(
          `Recibidas ${onlineDevices.length} máquinas online`,
          onlineDevices
        );

        const onlineUuids = onlineDevices.map((device) => device.uuid);
        setOnlineMachines(onlineUuids);

        onlineDevices.forEach((device) => {
          if (!device || !device.uuid) return;

          updateMachineStatus(device.uuid, {
            online: true,
            nro_maquina: device.id_machine || device.nro_maquina,
            statusMachine:
              device.statusMachine !== undefined
                ? device.statusMachine
                : "unknown",
          });
        });

        setMachines((prev) =>
          prev.map((machine) => ({
            ...machine,
            online: onlineUuids.includes(machine.uuid),
          }))
        );
      };

      // Handlers originales mantenidos EXACTAMENTE igual
      socket.on("listMachines", (data) => {
        console.log("Evento listMachines recibido:", data);
        updateOnlineStatus(data);
      });

      socket.on("devicesUpdated", (data) => {
        console.log("Evento devicesUpdated recibido:", data);
        updateOnlineStatus(data);
      });

      const handleMachineJoined = (newDevice: any) => {
        if (!newDevice || !newDevice.uuid) return;

        console.log("Máquina conectada:", newDevice);

        setOnlineMachines((prev) => {
          if (prev.includes(newDevice.uuid)) return prev;
          return [...prev, newDevice.uuid];
        });

        updateMachineStatus(newDevice.uuid, {
          online: true,
          nro_maquina: newDevice.id_machine || newDevice.nro_maquina,
          statusMachine:
            newDevice.statusMachine !== undefined
              ? newDevice.statusMachine
              : "unknown",
        });

        // toast.success(
        //   `Máquina ${
        //     newDevice.id_machine || newDevice.nro_maquina || "desconocida"
        //   } conectada`
        // );
      };

      const handleMachineLeft = (leftDevice: any) => {
        if (!leftDevice || !leftDevice.uuid) return;

        console.log("Máquina desconectada:", leftDevice);

        setOnlineMachines((prev) =>
          prev.filter((uuid) => uuid !== leftDevice.uuid)
        );

        updateMachineStatus(leftDevice.uuid, { online: false });

        toast.error(
          `Máquina ${
            leftDevice.id_machine || leftDevice.nro_maquina || "desconocida"
          } desconectada`
        );
      };

      // Eventos originales mantenidos EXACTAMENTE igual
      socket.on("machineJoined", handleMachineJoined);
      socket.on("machineLeft", handleMachineLeft);
      socket.on("machineConnected", handleMachineJoined);
      socket.on("machineDisconnected", handleMachineLeft);

      // Handler original mantenido
      socket.on("responseCommand", handleCommandResponse);

      // Solicitar lista original mantenida
      if (socket.connected) {
        console.log("Socket ya conectado, solicitando máquinas inmediatamente");
        getMachinesFromSocket();
      }

      return () => {
        socket.off("listMachines");
        socket.off("devicesUpdated");
        socket.off("machineJoined", handleMachineJoined);
        socket.off("machineLeft", handleMachineLeft);
        socket.off("machineConnected", handleMachineJoined);
        socket.off("machineDisconnected", handleMachineLeft);
        socket.off("responseCommand", handleCommandResponse);
        socket.off("allMachineStates");
        socket.off("machineStateUpdate");
      };
    }
  }, [
    socket,
    updateMachineStatus,
    handleCommandResponse,
    getMachinesFromSocket,
  ]);

  // Función sendCommand original mantenida EXACTAMENTE igual
  const sendCommand = useCallback(
    (
      commandData: CommandData,
      onResponse?: (response: CommandResponse) => void,
      timeout: number = 15000
    ) => {
      if (!isConnected) {
        toast.error("No hay conexión con el servidor de máquinas");
        return;
      }

      const commandKey = `${commandData.uuid}:${commandData.data.maquina}`;

      // Formato original mantenido
      const command = {
        command: commandData.command,
        data: commandData.data.data,
        uuid: commandData.uuid,
        maquina: commandData.data.maquina,
      };

      // Lógica original mantenida
      if (onResponse) {
        const timeoutId = setTimeout(() => {
          const pending = pendingCommandsRef.current.get(commandKey);
          if (pending) {
            onResponse({
              response: false,
              version: "",
              command: commandData.command,
              uuid: commandData.uuid,
              maquina: commandData.data.maquina,
              fechaDispositivo: new Date().toISOString(),
              request: {},
            });
            pendingCommandsRef.current.delete(commandKey);
          }
        }, timeout);

        pendingCommandsRef.current.set(commandKey, {
          onResponse,
          timeoutId,
        });
      }

      emit("sendCommand", command);
    },
    [isConnected, emit]
  );

  // useEffect original mantenido EXACTAMENTE igual
  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      connect();
    }

    return () => {
      pendingCommandsRef.current.forEach((command) => {
        clearTimeout(command.timeoutId);
      });
      pendingCommandsRef.current.clear();
      disconnect();
    };
  }, []);

  // useEffect original mantenido
  useEffect(() => {
    if (isConnected && machines.length === 0) {
      const retryTimer = setTimeout(() => {
        console.log("Reintentando obtener máquinas...");
        getMachinesFromSocket();
      }, 2000);

      return () => clearTimeout(retryTimer);
    }
  }, [isConnected, machines.length, getMachinesFromSocket]);

  // Valor del contexto actualizado con nueva función
  const value = {
    socket,
    isConnected,
    isMachineSessionStarted,
    isUserSessionStarted,
    machines,
    onlineMachines,
    connect,
    disconnect,
    disconnectMachine,
    emit,
    sendCommand,
    getMachinesFromSocket,
    subscribeMachineUpdates,
    unsubscribeMachineUpdates,
    // NUEVO: Función para registrar callbacks
    registerCleanupCallbacks,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};
